import { Badge } from '@/components/ui/badge';
import type { AdminOrganization } from '@/client/types/admin/users-orgs';

/**
 * Lifecycle badges. Prefer `deleted` and `admin_hold` — `active` is mostly
 * unset on this table, so we only badge it when explicitly 0 or 1.
 */
export const OrgStateBadges = ({
  org,
}: {
  org: Pick<
    AdminOrganization,
    | 'active'
    | 'deleted'
    | 'review'
    | 'admin_hold'
    | 'searchable'
    | 'vat_registered'
    | 'verified'
    | 'tester'
  > &
    Partial<
      Pick<
        AdminOrganization,
        'searchable' | 'vat_registered' | 'verified' | 'tester'
      >
    >;
}) => (
  <div className="flex flex-wrap gap-1">
    {org.deleted === 1 && <Badge variant="destructive">Deleted</Badge>}

    {org.active === 1 && (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>
    )}
    {org.active === 0 && <Badge variant="secondary">Inactive</Badge>}

    {org.tester === 1 && (
      <Badge
        variant="outline"
        className="border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-800"
        title="Test-mode organization"
      >
        Test
      </Badge>
    )}

    {org.review === 1 && (
      <Badge
        variant="outline"
        className="border-amber-500/40 bg-amber-500/10 text-amber-700"
        title="Awaiting name moderation — not publicly listable"
      >
        In review
      </Badge>
    )}

    {org.admin_hold === 1 && (
      <Badge
        variant="outline"
        className="border-red-500/40 bg-red-500/10 text-red-700"
        title="Admin hold — cannot transact"
      >
        On hold
      </Badge>
    )}

    {org.verified === 1 && (
      <Badge
        variant="outline"
        className="border-blue-500/40 bg-blue-500/10 text-blue-700"
        title="Verified organization"
      >
        Verified
      </Badge>
    )}

    {org.vat_registered === 1 && (
      <Badge
        variant="outline"
        className="border-violet-500/40 bg-violet-500/10 text-violet-700"
        title="VAT registered"
      >
        VAT
      </Badge>
    )}

    {org.searchable === 0 && (
      <Badge
        variant="outline"
        className="border-gray-400/40 bg-gray-100 text-gray-600"
        title="Not discoverable in the directory"
      >
        Hidden
      </Badge>
    )}
  </div>
);
